/**
 * useStudyGroupDoSync — thin React wrapper around the unified
 * `useDoSync` engine + `createStudyGroupDoAdapter`.
 *
 * One instance per open study-group WebSocket. Owns the Yjs provider
 * for the group room (carries the root Y.Doc + the LWW JSON channel
 * the engine listens on), the per-group IDB persistence handle, and
 * the React state that drives `notesSynced` gating in the notes editor.
 *
 * Sync engine wiring (decode, op-ack/op-rejected, persistence-error,
 * handshake, broadcast dispatch) lives in the engine + adapter. This
 * file is the React surface around them.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type YProvider from 'y-partyserver/provider';
import useYProvider from 'y-partyserver/react';

import { StudyGroupDoc } from '../../yjs/study-group-doc';
import {
  createStudyGroupDoAdapter,
  type SGLegacyMigrationClient,
} from '../engine/study-group-do-adapter';
import {
  isCredentialRejection,
  useDoSync,
  type WsCloseEvent,
} from '../engine/use-do-sync';
import { poolDrop } from '../group-connection-pool';
import { pushSyncMessage } from '../message-log';
import { perfLog, perfNow } from '../perf-log'; // [perf-probe]
import { useStudyGroupStore } from '../store/study-group-store';
import { getPartykitHost } from '../sync-config';
import { createYjsPersistence, type YjsPersistence } from '../yjs-persistence';
import {
  registerStudyGroupDoc,
  unregisterStudyGroupDoc,
} from './study-group-doc-registry';
import { registerGroupProviderSender } from './study-group-sender-registry';

const SG_PARTY = 'study-group-do';

export type UseStudyGroupDoSyncResult = {
  provider: YProvider | null;
  studyGroupDoc: StudyGroupDoc | null;
  notesSynced: boolean;
};

export function useStudyGroupDoSync(
  groupId: string | null,
  getToken?: () => Promise<string | null>,
  opts?: { userId?: string | null; sgLegacyClient?: SGLegacyMigrationClient },
): UseStudyGroupDoSyncResult {
  const ensureGroup = useStudyGroupStore((s) => s.ensureGroup);
  const setConnectionStatus = useStudyGroupStore((s) => s.setConnectionStatus);
  const hydrateGroupFromStorage = useStudyGroupStore((s) => s.hydrateGroupFromStorage);
  const setStoreNotesSynced = useStudyGroupStore((s) => s.setNotesSynced);

  // ── Provider ──────────────────────────────────────────────────────
  const provider = useYProvider({
    host: getPartykitHost(),
    party: SG_PARTY,
    room: groupId ?? '__noop__',
    options: {
      // CRITICAL: y-partyserver's BroadcastChannel key is
      // `${serverUrl}/${roomname}` and ignores query params; without
      // this, the root group provider would share a BC channel with
      // every per-note provider on the same group room and corrupt
      // each other's local Y.Doc state.
      disableBc: true,
      ...(getToken
        ? {
            params: async () => {
              const token = await getToken();
              return token ? { token } : {};
            },
          }
        : {}),
    },
  });

  // ── Slice priming + IDB rehydrate ─────────────────────────────────
  useEffect(() => {
    if (!groupId) return;
    ensureGroup(groupId);
    const perfT0 = perfNow(); // [perf-probe]
    void hydrateGroupFromStorage(groupId).then(() =>
      perfLog('sg:hydrate', perfT0, () => ({ groupId: groupId.slice(0, 8) })),
    );
  }, [groupId, ensureGroup, hydrateGroupFromStorage]);

  // ── Connection-state mirroring + auth-failure pool drop ───────────
  useEffect(() => {
    if (!groupId || !provider) {
      if (groupId) setConnectionStatus(groupId, 'disconnected');
      return;
    }
    setConnectionStatus(groupId, 'connecting');
    // Auth-failure close codes (4401 unauthorized / 4403 not a member)
    // are terminal — reconnecting will just be rejected again. Tear
    // down the pool entry immediately so `<StudyGroupSubscription>`
    // unmounts. Without this the y-partyserver provider auto-
    // reconnects forever after a leave. A token-LESS auth close is not a
    // rejection (the client could not mint a token — a locked iOS keychain in
    // the background): dropping the pool for it ended this group's sync for
    // the session, since the orchestrator re-acquires only when memberships
    // change. The engine (useDoSync below) retries those on a backoff
    // (FLASHKA-APP-ZH).
    const onClose = (event: WsCloseEvent | undefined) => {
      if (isCredentialRejection(event, provider)) {
        provider.disconnect();
        poolDrop(groupId);
      }
    };
    const onError = () => setConnectionStatus(groupId, 'error');
    provider.on('connection-close', onClose);
    provider.on('connection-error', onError);
    return () => {
      provider.off('connection-close', onClose);
      provider.off('connection-error', onError);
    };
  }, [groupId, provider, setConnectionStatus]);

  // ── Study-group Y.Doc wrapper ─────────────────────────────────────
  // Key the memo on `provider.doc` (stable per Y.Doc instance) rather
  // than `provider` (the y-partyserver/react ref can flip on connection
  // state changes). Prevents rebuilding StudyGroupDoc and re-running
  // downstream registration effects on every provider tick.
  const providerDoc = provider?.doc ?? null;
  const studyGroupDoc = useMemo<StudyGroupDoc | null>(
    () => (groupId && providerDoc ? new StudyGroupDoc(groupId, providerDoc) : null),
    [groupId, providerDoc],
  );

  // ── IndexedDB persistence (per-group room) ────────────────────────
  const idbRef = useRef<YjsPersistence | null>(null);
  const [idbSynced, setIdbSynced] = useState(false);
  const [wsSynced, setWsSynced] = useState<boolean>(() => provider?.synced ?? false);
  const [offlineFallback, setOfflineFallback] = useState(false);
  const notesSynced = wsSynced || (idbSynced && offlineFallback);

  // Mirror to the store so non-React readers see the same gating.
  useEffect(() => {
    if (groupId) setStoreNotesSynced(groupId, notesSynced);
  }, [groupId, notesSynced, setStoreNotesSynced]);

  useEffect(() => {
    setIdbSynced(false);
    setWsSynced(provider?.synced ?? false);
    setOfflineFallback(false);
    if (!groupId || !provider) return;
    const idb = createYjsPersistence(`flashka-sg-yjs:${groupId}`, provider.doc);
    idbRef.current = idb;
    const onLoaded = () => setIdbSynced(true);
    idb.on('synced', onLoaded);
    return () => {
      idb.off('synced', onLoaded);
      void idb.destroy();
      if (idbRef.current === idb) idbRef.current = null;
    };
  }, [groupId, provider]);

  // ── WebSocket sync tracking ───────────────────────────────────────
  useEffect(() => {
    if (!provider) return;
    const onSync = (isSynced: boolean) => {
      if (isSynced) setWsSynced(true);
    };
    provider.on('sync', onSync);
    if (provider.synced) setWsSynced(true);
    return () => {
      provider.off('sync', onSync);
    };
  }, [provider]);

  // Offline fallback: if IDB hydrated but WS hasn't synced within
  // OFFLINE_FALLBACK_MS, allow auto-create on IDB alone. Same rationale
  // as the user-doc path.
  useEffect(() => {
    if (!idbSynced || wsSynced) return;
    const id = setTimeout(() => setOfflineFallback(true), 8000);
    return () => clearTimeout(id);
  }, [idbSynced, wsSynced]);

  // ── Register the active SG doc so non-React callers can reach it ──
  useEffect(() => {
    if (!groupId || !studyGroupDoc) return;
    const unregister = registerStudyGroupDoc(groupId, studyGroupDoc);
    return unregister;
  }, [groupId, studyGroupDoc]);

  // ── Register a sender so `sync.studyGroups.send` works without
  //     threading the provider through props.
  useEffect(() => {
    if (!groupId || !provider) return;
    const unregister = registerGroupProviderSender(groupId, (msg) =>
      provider.sendMessage(msg),
    );
    return unregister;
  }, [groupId, provider]);

  // ── Log Yjs update traffic to the devtools message log ─────────────
  useEffect(() => {
    if (!groupId || !provider) return;
    const doc = provider.doc;
    const handler = (update: Uint8Array, origin: unknown) => {
      let direction: 'out' | 'in' | 'idb';
      let detail: string;
      if (origin === idbRef.current) {
        direction = 'idb';
        detail = `sg:${groupId} IDB replay`;
      } else if (origin != null && origin === provider) {
        direction = 'in';
        detail = `sg:${groupId} remote → local`;
      } else if (origin == null) {
        direction = 'out';
        detail = `sg:${groupId} local edit`;
      } else {
        direction = 'out';
        detail = `sg:${groupId} local (unknown origin)`;
      }
      pushSyncMessage({
        source: 'study-group-do',
        direction,
        type: 'yjs-update',
        ts: Date.now(),
        sizeBytes: update.byteLength,
        detail,
      });
    };
    doc.on('update', handler);
    return () => {
      doc.off('update', handler);
    };
  }, [groupId, provider]);

  // ── Cleanup Y.Doc reference on groupId change ─────────────────────
  useEffect(() => {
    return () => {
      if (groupId) unregisterStudyGroupDoc(groupId);
    };
  }, [groupId]);

  // ── Unified sync engine ───────────────────────────────────────────
  const userId = opts?.userId ?? null;
  const sgLegacyClient = opts?.sgLegacyClient;
  const adapter = useMemo(
    () =>
      groupId ? createStudyGroupDoAdapter({ groupId, userId, sgLegacyClient }) : null,
    [groupId, userId, sgLegacyClient],
  );

  useDoSync({
    provider,
    scopeLabel: 'study-group-do',
    roomId: groupId,
    adapter: adapter ?? noopAdapter,
  });

  return { provider, studyGroupDoc, notesSynced };
}

// Adapter used when groupId is null — the engine is gated by `roomId`
// being non-null so this is never actually invoked, but the hook still
// needs a value to pass.
const noopAdapter = {
  onProtocolMessage: () => false as const,
  subscribeDirty: () => () => {},
} as const;
